import {
  Box,
  Loader,
  Select as MantineSelect,
  type ComboboxLikeRenderOptionInput,
  type SelectProps as MantineSelectProps,
} from "@mantine/core";
import { IconAlertOctagon, IconChevronDown, IconSearch } from "@tabler/icons-react";
import clsx from "clsx";
import { useId } from "react";
import { Controller, type Control, type FieldValues, type Path } from "react-hook-form";
import styles from "./Select.module.scss";

export type SelectSize = "small" | "large";

export type SelectOption = {
  value: string;
  label: string;
  renderLabel?: React.ReactNode;
  disabled?: boolean;
};

export type SelectProps<T extends FieldValues = FieldValues> = Omit<
  MantineSelectProps,
  "error" | "data"
> & {
  label?: string;
  optional?: boolean;
  required?: boolean;
  hint?: string;
  helpIcon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  size?: SelectSize;
  error?: boolean | string;
  name: string; // Required field name for Controller
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  options: SelectOption[] | undefined;
  loading?: boolean;
  hasSearchIcon?: boolean;
  isInfiniteScroll?: boolean;
  hasMoreData?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  control?: Control<T>;
  onChangeCallback?: (value: string | null) => void;
  wrapperId?: string;
};

export const emptyOptionData = [{ value: "", label: "" }];
const LOADER_OPTION_VALUE = "__select_loader__";

export function Select<T extends FieldValues = FieldValues>({
  label,
  optional,
  required,
  hint,
  helpIcon,
  placeholder = "Select an option",
  disabled,
  size = "small",
  error,
  name,
  className,
  style,
  dataTestId,
  options = [],
  loading = false,
  hasSearchIcon = false,
  isInfiniteScroll = false,
  hasMoreData = true,
  isLoadingMore = false,
  onLoadMore,
  searchValue,
  onSearchChange,
  control,
  onChangeCallback,
  wrapperId,
  ...rest
}: SelectProps<T>) {
  const id = useId();
  const { renderOption, scrollAreaProps, searchable, ...mantineSelectProps } = rest;

  const baseData = options?.map((opt) => ({
    value: opt.value,
    label: opt.label,
    disabled: opt.disabled,
    renderLabel: opt.renderLabel,
  }));

  const shouldShowLoader = isInfiniteScroll && isLoadingMore;

  const data = shouldShowLoader
    ? [
        ...baseData,
        {
          value: LOADER_OPTION_VALUE,
          label: "",
          disabled: true,
        },
      ]
    : baseData;

  const shouldHandleInfiniteScroll = isInfiniteScroll && typeof onLoadMore === "function";

  const combinedScrollAreaProps = {
    type: "auto" as const,
    ...scrollAreaProps,
    style: { maxHeight: 220, overflowY: "auto" as const, ...scrollAreaProps?.style },
    ...(shouldHandleInfiniteScroll && {
      onBottomReached: () => {
        scrollAreaProps?.onBottomReached?.();
        if (hasMoreData && !isLoadingMore) {
          onLoadMore?.();
        }
      },
    }),
  };

  // Default renderOption that uses renderLabel when available
  const defaultRenderOption = (item: ComboboxLikeRenderOptionInput<any>) => {
    return item.option.renderLabel ?? item.option.label;
  };

  // Use user-provided renderOption or default
  const baseRenderOption = renderOption || defaultRenderOption;

  const combinedRenderOption = shouldShowLoader
    ? (
        optionInput: ComboboxLikeRenderOptionInput<{
          value: string;
          label: string;
        }>,
      ) => {
        if (optionInput.option.value === LOADER_OPTION_VALUE) {
          return (
            <div className={styles.loaderOption}>
              <Loader size="xs" color="var(--text-weak)" className={styles.loaderSpinner} />
              <span>Loading more...</span>
            </div>
          );
        }

        return baseRenderOption(optionInput);
      }
    : baseRenderOption;

  return (
    <Box
      id={wrapperId}
      className={clsx(styles.wrapper, className, styles[`wrapper-${size}`])}
      style={style}
    >
      {(label || optional) && (
        <Box className={styles.labelRow}>
          {label && (
            <label htmlFor={id} className={styles.labelText}>
              {label}
              {required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
              {helpIcon && <span className={styles.helpIcon}>{helpIcon}</span>}
            </label>
          )}
          {optional && <span className={styles.optionalText}>Optional</span>}
        </Box>
      )}

      <Controller
        name={name as Path<T>}
        control={control}
        render={({ field }) => (
          <MantineSelect
            {...field}
            onChange={(value) => {
              // Execute callback before changing the value
              if (onChangeCallback) {
                onChangeCallback(value);
              }
              // Call the original onChange
              field.onChange(value);
            }}
            data-testid={dataTestId}
            id={id}
            disabled={disabled}
            placeholder={placeholder}
            data={data}
            className={className}
            style={style}
            searchable={searchable}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            rightSection={
              loading ? (
                <Loader size="xs" color="var(--text-weak)" />
              ) : !hasSearchIcon ? (
                <IconChevronDown size={16} />
              ) : null
            }
            leftSection={hasSearchIcon ? <IconSearch size={16} /> : null}
            classNames={{
              input: clsx(styles.select, styles[`select-${size}`], error && styles.selectError),
              dropdown: styles.dropdown,
              option: clsx(styles.option, shouldShowLoader && styles.optionLoading),
            }}
            renderOption={combinedRenderOption}
            scrollAreaProps={combinedScrollAreaProps}
            {...mantineSelectProps}
          />
        )}
      />

      {typeof error === "string" && (
        <Box className={styles.errorMessage}>
          <IconAlertOctagon size={16} className={styles.errorIcon} />
          <span>{error}</span>
        </Box>
      )}

      {hint && <Box className={styles.hint}>{hint}</Box>}
    </Box>
  );
}
